import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { InvitationByTokenDocument } from "#app/lib/data";

export interface InvitationLookup {
  loading: boolean;
  details: {
    email: string;
    name: string;
    workspaceName: string;
    inviterName: string;
    alreadyCreated: boolean;
  } | null;
  invalid: boolean;
  invalidReason: string | null;
}

export function useInvitationByToken(token: string | undefined): InvitationLookup {
  const variables = useMemo(() => ({ token: token ?? "" }), [token]);

  const { data, loading, error } = useQuery(InvitationByTokenDocument, {
    variables,
    skip: !token,
    fetchPolicy: "network-only",
  });

  if (!token) {
    return { loading: false, details: null, invalid: true, invalidReason: "notFound" };
  }

  if (loading) {
    return { loading: true, details: null, invalid: false, invalidReason: null };
  }

  if (error || !data) {
    return { loading: false, details: null, invalid: true, invalidReason: "notFound" };
  }

  const result = data.invitationByToken;
  if (result.__typename === "InvitationDetails") {
    return {
      loading: false,
      details: {
        email: result.email,
        name: result.name,
        workspaceName: result.workspaceName,
        inviterName: result.inviterName,
        alreadyCreated: result.alreadyCreated,
      },
      invalid: false,
      invalidReason: null,
    };
  }

  return {
    loading: false,
    details: null,
    invalid: true,
    invalidReason: result.reason ?? "notFound",
  };
}
